import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const input = "mt-1 w-full border border-slate-200 px-2 py-1.5 text-sm text-slate-900";

function SentLog({ log }) {
  if (!log?.length) return null;
  return (
    <div className="border-t pt-3 space-y-1" data-testid="traeger-sent-log">
      <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500">Bisherige Sendungen</div>
      {log.slice().reverse().map((e, i) => (
        <div key={i} className={`text-xs flex items-center gap-1 ${e.ok ? "text-emerald-700" : "text-rose-700"}`}>
          {e.ok ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
          {new Date(e.at).toLocaleString("de-DE")} · {e.target === "client" ? "Kunde" : "Jobcenter"} · an {e.to}{e.cc?.length ? ` (CC ${e.cc.join(", ")})` : ""} · {e.ok ? "zugestellt an Mailserver" : e.error}
        </div>
      ))}
    </div>
  );
}

export default function TraegerSendDialog({ open, onClose, formId, beforeOpen }) {
  const [draft, setDraft] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [target, setTarget] = useState("client");

  useEffect(() => {
    if (!open) return undefined;
    let url;
    setDraft(null); setResult(null); setPdfUrl(null);
    (async () => {
      try {
        await beforeOpen?.();
        const [d, p] = await Promise.all([
          api.get(`/traeger-forms/form/${formId}/email-draft`, { params: { target } }),
          api.get(`/traeger-forms/form/${formId}/pdf`, { responseType: "blob", timeout: 180000 }),
        ]);
        url = URL.createObjectURL(new Blob([p.data], { type: "application/pdf" }));
        setPdfUrl(url);
        setDraft({ ...d.data, cc: d.data.cc.join(", ") });
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); onClose(); }
    })();
    return () => url && URL.revokeObjectURL(url);
  }, [open, formId]); // eslint-disable-line react-hooks/exhaustive-deps

  const switchTarget = async (t) => {
    setTarget(t); setResult(null);
    try { const d = await api.get(`/traeger-forms/form/${formId}/email-draft`, { params: { target: t } }); setDraft({ ...d.data, cc: d.data.cc.join(", ") }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const send = async () => {
    setBusy(true); setResult(null);
    try {
      const cc = draft.cc.split(/[,;\s]+/).filter(Boolean);
      await api.post(`/traeger-forms/form/${formId}/send`, { to: draft.to, cc, subject: draft.subject, body: draft.body, target }, { timeout: 90000 });
      setResult({ ok: true, msg: `Erfolgreich versendet an ${draft.to}` });
      toast.success("Trägerbestätigung versendet");
      const d = await api.get(`/traeger-forms/form/${formId}/email-draft`, { params: { target } });
      setDraft((x) => ({ ...x, sent_log: d.data.sent_log }));
    } catch (e) {
      setResult({ ok: false, msg: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="traeger-send-dialog">
        <DialogHeader><DialogTitle>Vorschau & Versand</DialogTitle></DialogHeader>
        {!draft ? (
          <div className="p-8 flex items-center gap-2 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> Vorschau wird erstellt …</div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-5">
            <div className="space-y-2">
              <iframe title="Vorschau" src={pdfUrl} className="w-full h-[66vh] border border-slate-200 bg-slate-50" data-testid="traeger-send-preview" />
              <a href={pdfUrl} target="_blank" rel="noreferrer" className="text-xs underline underline-offset-4 text-slate-600" data-testid="traeger-send-preview-open">Vorschau wird nicht angezeigt? PDF in neuem Tab öffnen</a>
            </div>
            <div className="space-y-3">
              <div className="flex border border-slate-200 w-fit" data-testid="traeger-send-target">
                {[["client", "An den Kunden"], ["jobcenter", "An Jobcenter / Agentur"]].map(([k, l]) => (
                  <button key={k} onClick={() => switchTarget(k)} data-testid={`traeger-send-target-${k}`}
                    className={`px-4 py-2 text-xs transition-colors ${target === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}>{l}</button>
                ))}
              </div>
              <label className="block text-xs text-slate-500">{target === "client" ? "An (E-Mail des Kunden aus der Akte)" : "An (Arbeitsvermittler/in)"}
                <input value={draft.to} onChange={(e) => setDraft({ ...draft, to: e.target.value })} className={input} placeholder={target === "client" ? "kunde@beispiel.de" : "vermittler@jobcenter-ge.de"} data-testid="traeger-send-to" />
              </label>
              <label className="block text-xs text-slate-500">Kopie (CC, mit Komma trennen)
                <input value={draft.cc} onChange={(e) => setDraft({ ...draft, cc: e.target.value })} className={input} data-testid="traeger-send-cc" />
              </label>
              <label className="block text-xs text-slate-500">Betreff
                <input value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} className={input} data-testid="traeger-send-subject" />
              </label>
              <label className="block text-xs text-slate-500">Text
                <textarea rows={11} value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} className={input} data-testid="traeger-send-body" />
              </label>
              <div className="text-xs text-slate-500">Anhang: ausgefülltes PDF, genau wie links in der Vorschau</div>
              {result && (
                <div className={`flex items-center gap-2 p-3 text-sm border ${result.ok ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-rose-200 bg-rose-50 text-rose-800"}`} data-testid="traeger-send-result">
                  {result.ok ? <CheckCircle2 size={16} /> : <XCircle size={16} />} {result.ok ? result.msg : `Fehler beim Senden: ${result.msg}`}
                </div>
              )}
              <button onClick={send} disabled={busy || !draft.to} data-testid="traeger-send-submit"
                className="inline-flex items-center gap-2 bg-[#0a788c] text-white px-5 py-2.5 text-sm hover:bg-[#086373] transition-colors disabled:opacity-50">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Jetzt senden
              </button>
              <SentLog log={draft.sent_log} />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
